/**
 * Error Deduplication Utility
 * Prevents console spam from error loops by suppressing duplicate errors
 * within a configurable time window.
 */

const recentErrors = new Map<string, number>();
const ERROR_WINDOW_MS = 5000; // 5 seconds between identical errors

/**
 * Log an error only if it hasn't been logged recently with the same key.
 * @param key Unique identifier for this error type (e.g., "CashRegister.fetch")
 * @param error The error object or message
 * @param context Optional additional context to log
 */
export function logDedupError(key: string, error: unknown, context?: Record<string, unknown>): boolean {
  const now = Date.now();
  const lastLogged = recentErrors.get(key);
  
  if (lastLogged && now - lastLogged < ERROR_WINDOW_MS) {
    return false; // Suppress duplicate error
  }
  
  recentErrors.set(key, now);
  
  if (context) {
    console.error(`[${key}]`, error, context);
  } else {
    console.error(`[${key}]`, error);
  }
  
  return true; // Error was logged
}

/**
 * Clear all tracked errors (useful for testing or reset scenarios)
 */
export function clearErrorHistory(): void {
  recentErrors.clear();
}

/**
 * Check if we're online before attempting network operations
 */
export function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}
